type Environment = 'local' | 'dev' | 'stage' | 'prod';

interface Config {
	// Environment
	ENV: Environment;
	IS_PROD: boolean;
	SENTRY_ENABLED: boolean;
	SENTRY_RELEASE?: string;
	SENTRY_TRACES_SAMPLE_RATE: number;

	// IMS (Adobe Identity Management)
	IMS_CLIENT_ID: string;
	IMS_HOST: string;
	IMS_AUTH_PATH: string;
	IMS_PROFILE_PATH: string;
	IMS_LOGOUT_PATH: string;

	// UI Proxy
	UI_PROXY_URL: string;

	// Express (Adobe Express)
	EXPRESS_WEB_HOST: string;
	EXPRESS_API_HOST: string;
	EXPRESS_API_KEY: string;

	// Beacon Bridge WebSocket
	BEACON_API_WS_BRIDGE: string;

	// Bridgeway HTTP API
	BRIDGEWAY_API_URL: string;

	// Beacon API URL (for direct API calls)
	beaconApiUrl: string;

	// UI Proxy URL (aliased for convenience)
	uiProxyUrl: string;

	// ElevenLabs
	ELEVENLABS_AGENT_ID: string;

	// LangGraph
	LANGGRAPH_PLATFORM_API_BASE_URL: string;

	// Square OAuth
	SQUARE_APPLICATION_ID: string;
	SQUARE_OAUTH_BASE_URL: string;

	// Square Sandbox OAuth (for test-mode toggle)
	SQUARE_SANDBOX_APPLICATION_ID: string;
	SQUARE_SANDBOX_OAUTH_BASE_URL: string;

	// Instagram OAuth (Meta App)
	META_APP_CLIENT_ID: string;

	// Waitlist iframe URL (landing page)
	WAITLIST_URL: string;
}

/**
 * A Square OAuth value for LOCAL development only, overridable from `services/ts/web/.env.local`.
 *
 * Only `localConfig` calls this. The deployed blocks keep their literals, so no override can
 * reach dev, stage or production however the build is run — Vite inlines `import.meta.env` at
 * build time, and a developer's untracked `.env.local` is not part of any deployed build.
 *
 * Square treats its sandbox and live hosts as separate worlds with separate credentials, so a
 * local machine has no reason to hold a live application id. Both slots default to sandbox.
 */
function squareEnv(name: string, fallback: string): string {
	const value = (import.meta.env[name] as string | undefined)?.trim();
	return value || fallback;
}

const devConfig: Config = {
	ENV: 'dev',
	IS_PROD: false,
	SENTRY_ENABLED: true,
	SENTRY_TRACES_SAMPLE_RATE: 0.1,

	// IMS
	IMS_CLIENT_ID: 'incubator-beacon-ui-stage',
	IMS_HOST: 'https://ims-na1-stg1.adobelogin.com',
	IMS_AUTH_PATH: '/ims/authorize/v2',
	IMS_PROFILE_PATH: '/ims/profile/v1',
	IMS_LOGOUT_PATH: '/ims/logout/v1',

	// UI Proxy
	UI_PROXY_URL: 'https://beacon-ui-proxy-dev.adobe.io',

	// Express
	EXPRESS_WEB_HOST: 'https://express-stage.adobe.com',
	EXPRESS_API_HOST: 'https://api.stage.schedule.adobe.com',
	EXPRESS_API_KEY: 'CCSchedule1',

	// Beacon Bridge
	BEACON_API_WS_BRIDGE: 'wss://beacon-bridge-dev.adobe.io/ws',

	// Bridgeway HTTP API
	BRIDGEWAY_API_URL: 'https://beacon-dbapi-dev-public.adobe.io',

	// Beacon API URL
	beaconApiUrl: 'https://beacon-dbapi-dev-public.adobe.io',

	// UI Proxy URL alias
	uiProxyUrl: 'https://beacon-ui-proxy-dev.adobe.io',

	// ElevenLabs
	ELEVENLABS_AGENT_ID: 'agent_7901kfp4hb1afktszrj70gpyxdvm',

	// LangGraph
	LANGGRAPH_PLATFORM_API_BASE_URL: 'https://beacon-langgraph-dev-corp.adobe.io',

	// Square OAuth (use sandbox for non-prod)
	SQUARE_APPLICATION_ID: 'sq0idp-4sX8iRn0ezvh0xVoidi3qA',
	SQUARE_OAUTH_BASE_URL: 'https://connect.squareup.com',

	// Square Sandbox
	SQUARE_SANDBOX_APPLICATION_ID: 'sandbox-sq0idb-yIWhJGLbtgCn9YTGQ3nZoQ',
	SQUARE_SANDBOX_OAUTH_BASE_URL: 'https://connect.squareupsandbox.com',

	// Instagram OAuth (Meta App)
	META_APP_CLIENT_ID: '1584697292632272',

	// Waitlist
	WAITLIST_URL:
		'https://beacon-waitlist-apehfub0ebfjg7f2.westus2-01.azurewebsites.net',
};

const stageConfig: Config = {
	ENV: 'stage',
	IS_PROD: false,
	SENTRY_ENABLED: true,
	SENTRY_TRACES_SAMPLE_RATE: 0.1,

	// IMS
	IMS_CLIENT_ID: 'incubator-beacon-ui-stage',
	IMS_HOST: 'https://ims-na1-stg1.adobelogin.com',
	IMS_AUTH_PATH: '/ims/authorize/v2',
	IMS_PROFILE_PATH: '/ims/profile/v1',
	IMS_LOGOUT_PATH: '/ims/logout/v1',

	// UI Proxy
	UI_PROXY_URL: 'https://beacon-ui-proxy-stage.adobe.io',

	// Express
	EXPRESS_WEB_HOST: 'https://express-stage.adobe.com',
	EXPRESS_API_HOST: 'https://api.stage.schedule.adobe.com',
	EXPRESS_API_KEY: 'CCSchedule1',

	// Beacon Bridge
	BEACON_API_WS_BRIDGE: 'wss://beacon-bridge-stage.adobe.io/ws',

	// Bridgeway HTTP API
	BRIDGEWAY_API_URL: 'https://beacon-dbapi-stage-public.adobe.io',

	// Beacon API URL
	beaconApiUrl: 'https://beacon-dbapi-stage-public.adobe.io',

	// UI Proxy URL alias
	uiProxyUrl: 'https://beacon-ui-proxy-stage.adobe.io',

	// ElevenLabs
	ELEVENLABS_AGENT_ID: 'agent_7901kfp4hb1afktszrj70gpyxdvm',

	// LangGraph
	LANGGRAPH_PLATFORM_API_BASE_URL:
		'https://beacon-langgraph-stage-corp.adobe.io',

	// Square OAuth (use sandbox for non-prod)
	SQUARE_APPLICATION_ID: 'sq0idp-VhbypnNWjhengii4qm8VVg',
	SQUARE_OAUTH_BASE_URL: 'https://connect.squareup.com',

	// Square Sandbox
	SQUARE_SANDBOX_APPLICATION_ID: 'sandbox-sq0idb-v1bQwFGeaM3sCNpaP9Tpzw',
	SQUARE_SANDBOX_OAUTH_BASE_URL: 'https://connect.squareupsandbox.com',

	// Instagram OAuth (Meta App)
	META_APP_CLIENT_ID: '4132128620410650',

	// Waitlist
	WAITLIST_URL:
		'https://beacon-waitlist-apehfub0ebfjg7f2.westus2-01.azurewebsites.net',
};

const prodConfig: Config = {
	ENV: 'prod',
	IS_PROD: true,
	SENTRY_ENABLED: true,
	SENTRY_TRACES_SAMPLE_RATE: 0.1,

	// IMS
	IMS_CLIENT_ID: 'incubator-beacon-ui-prod',
	IMS_HOST: 'https://ims-na1.adobelogin.com',
	IMS_AUTH_PATH: '/ims/authorize/v2',
	IMS_PROFILE_PATH: '/ims/profile/v1',
	IMS_LOGOUT_PATH: '/ims/logout/v1',

	// UI Proxy
	UI_PROXY_URL: 'https://beacon-ui-proxy.adobe.io',

	// Express
	EXPRESS_WEB_HOST: 'https://express.adobe.com',
	EXPRESS_API_HOST: 'https://api.schedule.adobe.com',
	EXPRESS_API_KEY: 'CCSchedule1',

	// Beacon Bridge
	BEACON_API_WS_BRIDGE: 'wss://beacon-bridge-public.adobe.io/ws',

	// Bridgeway HTTP API
	BRIDGEWAY_API_URL: 'https://beacon-dbapi-public.adobe.io',

	// Beacon API URL
	beaconApiUrl: 'https://beacon-dbapi-public.adobe.io',

	// UI Proxy URL alias
	uiProxyUrl: 'https://beacon-ui-proxy.adobe.io',

	// ElevenLabs
	ELEVENLABS_AGENT_ID: 'agent_7001kbdmqa3gfyr9wndd3hpfs913',

	// LangGraph
	LANGGRAPH_PLATFORM_API_BASE_URL: 'https://beacon-langgraph.adobe.io',

	// Square OAuth (production)
	SQUARE_APPLICATION_ID: 'sq0idp--9UAdyujdXxlNjZ-D6JZTQ',
	SQUARE_OAUTH_BASE_URL: 'https://connect.squareup.com',

	// Square Sandbox
	SQUARE_SANDBOX_APPLICATION_ID: 'sandbox-sq0idb-oH0crXEmVZjz8OvDIBnCww',
	SQUARE_SANDBOX_OAUTH_BASE_URL: 'https://connect.squareupsandbox.com',

	// Instagram OAuth (Meta App)
	META_APP_CLIENT_ID: '2125717204547849',

	// Waitlist
	WAITLIST_URL: 'https://beacon-waitlist-prod.azurewebsites.net',
};

const localConfig: Config = {
	ENV: 'local',
	IS_PROD: false,
	SENTRY_ENABLED: true,
	SENTRY_TRACES_SAMPLE_RATE: 0.1,

	// IMS (use stage)
	IMS_CLIENT_ID: 'incubator-beacon-ui-stage',
	IMS_HOST: 'https://ims-na1-stg1.adobelogin.com',
	IMS_AUTH_PATH: '/ims/authorize/v2',
	IMS_PROFILE_PATH: '/ims/profile/v1',
	IMS_LOGOUT_PATH: '/ims/logout/v1',

	// UI Proxy (local)
	UI_PROXY_URL: 'http://localhost:3006',

	// Express (use stage)
	EXPRESS_WEB_HOST: 'https://express-stage.adobe.com',
	EXPRESS_API_HOST: 'https://api.stage.schedule.adobe.com',
	EXPRESS_API_KEY: 'CCSchedule1',

	// Beacon Bridge (local)
	BEACON_API_WS_BRIDGE: 'ws://localhost:8080/ws',

	// Bridgeway HTTP API (local)
	BRIDGEWAY_API_URL: 'http://localhost:8084',

	// Beacon API URL (local)
	beaconApiUrl: 'http://localhost:8084',

	// UI Proxy URL alias (local)
	uiProxyUrl: 'http://localhost:3006',

	// ElevenLabs (local via Ngrok)
	ELEVENLABS_AGENT_ID: 'agent_9501k2amhy1tfw8tthz39nc5f1h9',

	// LangGraph (local)
	LANGGRAPH_PLATFORM_API_BASE_URL: 'http://localhost:2024',

	// Square OAuth. Local points at Square's SANDBOX in both slots on purpose: the
	// non-sandbox pair is what a Connect click uses when the sandbox toggle is off, and
	// aiming that at connect.squareup.com from a developer machine means one stray click
	// starts a real-merchant authorization. There is no local use for the live host, so it
	// is not reachable from here at all. Deployed environments keep their own live values.
	SQUARE_APPLICATION_ID: squareEnv(
		'VITE_SQUARE_APPLICATION_ID',
		'sandbox-sq0idb-G0N6rirUXRgq3KgeWFjoWA',
	),
	SQUARE_OAUTH_BASE_URL: squareEnv(
		'VITE_SQUARE_OAUTH_BASE_URL',
		'https://connect.squareupsandbox.com',
	),

	// Square Sandbox. Overridable so a developer can point at their own sandbox
	// application without editing this file, which would change it for everyone.
	SQUARE_SANDBOX_APPLICATION_ID: squareEnv(
		'VITE_SQUARE_SANDBOX_APPLICATION_ID',
		'sandbox-sq0idb-G0N6rirUXRgq3KgeWFjoWA',
	),
	SQUARE_SANDBOX_OAUTH_BASE_URL: squareEnv(
		'VITE_SQUARE_SANDBOX_OAUTH_BASE_URL',
		'https://connect.squareupsandbox.com',
	),

	// Instagram OAuth (Meta App)
	META_APP_CLIENT_ID: '1584697292632272',

	// Waitlist
	WAITLIST_URL:
		'https://beacon-waitlist-apehfub0ebfjg7f2.westus2-01.azurewebsites.net',
};

const PROD_HOSTNAMES = new Set(['beacon-ui.adobe.io', 'bridgeway.adobe.com']);
const DEV_HOSTNAMES = new Set(['beacon-ui-dev.adobe.io']);
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1']);
const STAGE_HOSTNAMES = new Set([
	'beacon-ui-stage.adobe.io',
	'bridgeway-stage.corp.adobe.com',
]);

/**
 * Reports an unrecognised hostname straight to the stage ui-proxy's Splunk route.
 * A plain import of `services/ts/web/src/logger.ts` would close a cycle back onto this module's
 * own `config` export (still uninitialised while `getConfig()` is running), so this
 * sends the same shape of payload directly instead.
 */
function reportUnrecognizedHostname(hostname: string): void {
	const message = `Unrecognised hostname "${hostname}". Falling back to the stage config. Add it to PROD_HOSTNAMES, DEV_HOSTNAMES, LOCAL_HOSTNAMES or STAGE_HOSTNAMES if that is wrong.`;
	console.warn(message);
	fetch(`${stageConfig.UI_PROXY_URL}/splunk-vite-proxy/proxy`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ level: 'warn', message }),
	}).catch(() => {
		// Best-effort: the console.warn above already surfaced this locally.
	});
}

function getEnvironment(): Environment {
	const hostname = window.location.hostname;

	// Production hostname fallback
	if (PROD_HOSTNAMES.has(hostname)) {
		return 'prod';
	}

	// Dev hostname fallback
	if (DEV_HOSTNAMES.has(hostname)) {
		return 'dev';
	}

	// Local: localhost or 127.0.0.1
	if (LOCAL_HOSTNAMES.has(hostname)) {
		return 'local';
	}

	// Stage, or any other hostname. An unlisted production host lands here too,
	// which sends its browser logs to the stage ui-proxy and out of the
	// beacon_prod index. Name the hostname so the condition is visible instead
	// of silent, and report it to Splunk (not just the browser console) since
	// this is the exact failure mode of missing log attribution.
	if (!STAGE_HOSTNAMES.has(hostname)) {
		reportUnrecognizedHostname(hostname);
	}
	return 'stage';
}

function getConfig(): Config {
	const env = getEnvironment();
	if (env === 'prod') return prodConfig;
	if (env === 'dev') return devConfig;
	if (env === 'local') return localConfig;
	return stageConfig;
}

export const config = getConfig();
